import {
  Grid,
  Paper,
  Typography,
  Button,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  StaticPropertyKey,
  VictimInfoKeys,
} from '@bvi/api-interfaces/entity/case-property';
import { AdditionalFilterParameter } from '@bvi/api-interfaces/request/case';
import { ICaseStatisticsData } from '@bvi/api-interfaces/response/case';
import { Autocomplete, Select2 } from '@bvi/common-components';
import { useFormOptions } from '@bvi/form-utils';

import {
  ISearchFormDataSchema,
  ITerritoryValue,
  OnOptionsLoad,
  prependOrganizationTypeBulkOptions,
  useOrganizationTypeBulkSelection,
  useSearchContext,
  parentAgeCategories,
  formatNumber,
  getResolutionDateForEndYear,
  getResolutionDateForStartYear,
  getResolutionYearOptions,
  getYearFromResolutionDate,
} from '../..';
import { getUniqueLocations } from '../../lib/getUniqLocations';
import { i18nInstance } from '../../locales';

import { styles } from './styles';

type InitialFilterProperties = {
  onOptionsLoad: OnOptionsLoad;
  isFormLoading?: boolean;
  statisticsData: ICaseStatisticsData;
  isFetching: boolean;
};

export const InitialFilter: React.FC<InitialFilterProperties> = (
  properties,
) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { onOptionsLoad, isFormLoading, statisticsData, isFetching } =
    properties;

  const { onSubmit, includeStatisticsData } = useSearchContext();
  const { t } = useTranslation('', {
    i18n: i18nInstance,
  });

  const {
    control,
    formState: { errors },
    getValues,
    reset,
    resetField,
    handleSubmit,
    setValue,
  } = useFormContext<ISearchFormDataSchema>();

  const organizationValue = useWatch({
    control,
    name: StaticPropertyKey.ORGANIZATION,
  });

  const subsidiaryValue = useWatch({
    control,
    name: StaticPropertyKey.DIOCESE,
  });

  useOrganizationTypeBulkSelection(onOptionsLoad);

  const ageCategoryOptions = useFormOptions(
    parentAgeCategories,
    'ageCategory',
    'ageCategoryDescription',
  );

  const handleResetOnRegionChange = (value: Array<ITerritoryValue>) => {
    const selectedCountries = getValues(StaticPropertyKey.COUNTRY);
    const countries = value.map((region) => region.country).filter(Boolean);

    const { shouldUpdate: shouldUpdateCountries, list: uniqCountries } =
      getUniqueLocations(selectedCountries, countries);

    if (shouldUpdateCountries) {
      resetField(StaticPropertyKey.COUNTRY, {
        defaultValue: uniqCountries,
      });
    }
    resetField(StaticPropertyKey.STATE);
  };

  const handleResetOnStateChange = (value: Array<ITerritoryValue>) => {
    const selectedCountries = getValues(StaticPropertyKey.COUNTRY);
    const countries = value.map((state) => state.country).filter(Boolean);
    const selectedRegions = getValues(StaticPropertyKey.REGION);
    const regions = value.map((state) => state.region).filter(Boolean);

    const { shouldUpdate: shouldUpdateCountries, list: uniqCountries } =
      getUniqueLocations(selectedCountries, countries);
    const { shouldUpdate: shouldUpdateRegions, list: uniqRegions } =
      getUniqueLocations(selectedRegions, regions);

    if (shouldUpdateCountries) {
      resetField(StaticPropertyKey.COUNTRY, {
        defaultValue: uniqCountries,
      });
    }
    if (shouldUpdateRegions) {
      resetField(StaticPropertyKey.REGION, {
        defaultValue: uniqRegions,
      });
    }
  };

  const handleResetRefineAgeCategory = () => {
    resetField(AdditionalFilterParameter.REFINE_AGE_CATEGORY, {
      defaultValue: [],
    });
  };

  const resolutionDateLow = useWatch({
    control,
    name: AdditionalFilterParameter.RESOLUTION_DATE_LOW,
  });
  const resolutionDateHigh = useWatch({
    control,
    name: AdditionalFilterParameter.RESOLUTION_DATE_HIGH,
  });
  const yearOptions = useMemo(() => getResolutionYearOptions(), []);
  const startYear = getYearFromResolutionDate(resolutionDateLow);
  const endYear = getYearFromResolutionDate(resolutionDateHigh);

  const handleStartYearChange = (event: SelectChangeEvent<string>) => {
    const nextValue = event.target.value;

    if (nextValue === '') {
      setValue(AdditionalFilterParameter.RESOLUTION_DATE_LOW, null, {
        shouldDirty: true,
      });
      return;
    }

    const nextStartYear = Number(nextValue);
    setValue(
      AdditionalFilterParameter.RESOLUTION_DATE_LOW,
      getResolutionDateForStartYear(nextStartYear),
      { shouldDirty: true },
    );

    if (endYear !== '' && nextStartYear > endYear) {
      setValue(
        AdditionalFilterParameter.RESOLUTION_DATE_HIGH,
        getResolutionDateForEndYear(nextStartYear),
        { shouldDirty: true },
      );
    }
  };

  const handleEndYearChange = (event: SelectChangeEvent<string>) => {
    const nextValue = event.target.value;

    if (nextValue === '') {
      setValue(AdditionalFilterParameter.RESOLUTION_DATE_HIGH, null, {
        shouldDirty: true,
      });
      return;
    }

    const nextEndYear = Number(nextValue);
    setValue(
      AdditionalFilterParameter.RESOLUTION_DATE_HIGH,
      getResolutionDateForEndYear(nextEndYear),
      { shouldDirty: true },
    );

    if (startYear !== '' && nextEndYear < startYear) {
      setValue(
        AdditionalFilterParameter.RESOLUTION_DATE_LOW,
        getResolutionDateForStartYear(nextEndYear),
        { shouldDirty: true },
      );
    }
  };

  const yearSelectMenuProperties = {
    PaperProps: {
      style: {
        maxHeight: 300,
      },
    },
  };

  const renderForm = () => {
    return (
      <>
        <Grid container item xs={12} columnSpacing={3} my={2}>
          <Grid item xs={3}>
            <Autocomplete
              name={StaticPropertyKey.COUNTRY}
              onOptionsLoad={async (data) =>
                await onOptionsLoad(StaticPropertyKey.COUNTRY, data)
              }
              resetOnChange={() => {
                reset((formValues) => ({
                  ...formValues,
                  [StaticPropertyKey.REGION]: [],
                  [StaticPropertyKey.STATE]: [],
                }));
              }}
              variant="filled"
              multiple
              isPrefetching={isFormLoading}
              label={t(`form.${StaticPropertyKey.COUNTRY}.label`)}
              placeholder={t(`form.${StaticPropertyKey.COUNTRY}.placeholder`)}
              helperText={<>{errors.country?.message}</>}
              error={Boolean(errors.country)}
            />
          </Grid>
          <Grid item xs={3}>
            <Autocomplete<ITerritoryValue, true>
              name={StaticPropertyKey.REGION}
              onOptionsLoad={async (data) => {
                const countryIds = getValues('country');

                return await onOptionsLoad(StaticPropertyKey.REGION, {
                  ...data,
                  countryIds,
                });
              }}
              resetOnChange={handleResetOnRegionChange}
              variant="filled"
              multiple
              isPrefetching={isFormLoading}
              label={t(`form.${StaticPropertyKey.REGION}.label`)}
              placeholder={t(`form.${StaticPropertyKey.REGION}.placeholder`)}
              helperText={<>{errors[StaticPropertyKey.REGION]?.message}</>}
              error={Boolean(errors[StaticPropertyKey.REGION])}
            />
          </Grid>
          <Grid item xs={3}>
            <Autocomplete<ITerritoryValue, true>
              name={StaticPropertyKey.STATE}
              onOptionsLoad={async (data) => {
                const [countryIds, regionIds] = getValues([
                  'country',
                  'region',
                ]);

                return await onOptionsLoad(StaticPropertyKey.STATE, {
                  ...data,
                  countryIds,
                  regionIds,
                });
              }}
              resetOnChange={handleResetOnStateChange}
              variant="filled"
              multiple
              isPrefetching={isFormLoading}
              label={t(`form.${StaticPropertyKey.STATE}.label`)}
              placeholder={t(`form.${StaticPropertyKey.STATE}.placeholder`)}
              helperText={<>{errors[StaticPropertyKey.STATE]?.message}</>}
              error={Boolean(errors[StaticPropertyKey.STATE])}
            />
          </Grid>
          <Grid item xs={3}>
            <Select2
              name={VictimInfoKeys.AGE_CATEGORY}
              variant="filled"
              fullWidth
              multiple
              items={ageCategoryOptions}
              label={t(`form.${VictimInfoKeys.AGE_CATEGORY}.label`)}
              placeholder={t(`form.${VictimInfoKeys.AGE_CATEGORY}.placeholder`)}
              helperText={<>{errors[VictimInfoKeys.AGE_CATEGORY]?.message}</>}
              error={Boolean(errors[VictimInfoKeys.AGE_CATEGORY])}
              resetOnChange={handleResetRefineAgeCategory}
            />
          </Grid>
        </Grid>
        <Grid
          container
          item
          xs={12}
          columnSpacing={3}
          my={2}
          sx={styles.orgRow}
        >
          <Grid item xs={3}>
            <Box sx={styles.filterFieldColumn}>
              <Autocomplete
                name={StaticPropertyKey.ORGANIZATION_TYPE}
                onOptionsLoad={async (data) => {
                  const response = await onOptionsLoad(
                    StaticPropertyKey.ORGANIZATION_TYPE,
                    data,
                  );

                  return prependOrganizationTypeBulkOptions(
                    response,
                    data.search,
                  );
                }}
                variant="filled"
                multiple
                isPrefetching={isFormLoading}
                label={t(`form.${StaticPropertyKey.ORGANIZATION_TYPE}.label`)}
                placeholder={t(
                  `form.${StaticPropertyKey.ORGANIZATION_TYPE}.placeholder`,
                )}
              />
              <Box sx={styles.filterFieldExcludeRow} aria-hidden />
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box sx={styles.filterFieldColumn}>
              <Autocomplete
                name={StaticPropertyKey.ORGANIZATION}
                onOptionsLoad={async (data) =>
                  await onOptionsLoad(StaticPropertyKey.ORGANIZATION, data)
                }
                variant="filled"
                label={t(`form.${StaticPropertyKey.ORGANIZATION}.label`)}
                placeholder={t(
                  `form.${StaticPropertyKey.ORGANIZATION}.placeholder`,
                )}
                resetOnChange={() => {
                  setValue(
                    AdditionalFilterParameter.EXCLUDE_ORGANIZATION,
                    false,
                    { shouldDirty: true },
                  );
                }}
              />
              <Box sx={styles.filterFieldExcludeRow}>
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={
                    <Controller
                      name={AdditionalFilterParameter.EXCLUDE_ORGANIZATION}
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          size="small"
                          checked={field.value ?? false}
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                          disabled={!organizationValue?.id}
                        />
                      )}
                    />
                  }
                  label={t('form.excludeOrganization.label')}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box sx={styles.filterFieldColumn}>
              <Autocomplete
                name={StaticPropertyKey.DIOCESE}
                onOptionsLoad={async (data) =>
                  await onOptionsLoad(StaticPropertyKey.DIOCESE, data)
                }
                variant="filled"
                label={t(`form.${StaticPropertyKey.DIOCESE}.label`)}
                placeholder={t(`form.${StaticPropertyKey.DIOCESE}.placeholder`)}
                resetOnChange={() => {
                  setValue(
                    AdditionalFilterParameter.EXCLUDE_SUBSIDIARY,
                    false,
                    { shouldDirty: true },
                  );
                }}
              />
              <Box sx={styles.filterFieldExcludeRow}>
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={
                    <Controller
                      name={AdditionalFilterParameter.EXCLUDE_SUBSIDIARY}
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          size="small"
                          checked={field.value ?? false}
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                          disabled={!subsidiaryValue?.id}
                        />
                      )}
                    />
                  }
                  label={t('form.excludeSubsidiary.label')}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box sx={styles.filterFieldColumn}>
              <Box sx={styles.yearSelects}>
                <FormControl fullWidth variant="filled">
                  <InputLabel id="resolution-start-year">
                    {t('form.resolutionYears.start')}
                  </InputLabel>
                  <Select
                    labelId="resolution-start-year"
                    label={t('form.resolutionYears.start')}
                    value={startYear === '' ? '' : String(startYear)}
                    onChange={handleStartYearChange}
                    MenuProps={yearSelectMenuProperties}
                  >
                    <MenuItem value="">
                      <em>{t('form.resolutionYears.placeholder')}</em>
                    </MenuItem>
                    {yearOptions.map((year) => (
                      <MenuItem key={year} value={String(year)}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth variant="filled">
                  <InputLabel id="resolution-end-year">
                    {t('form.resolutionYears.end')}
                  </InputLabel>
                  <Select
                    labelId="resolution-end-year"
                    label={t('form.resolutionYears.end')}
                    value={endYear === '' ? '' : String(endYear)}
                    onChange={handleEndYearChange}
                    MenuProps={yearSelectMenuProperties}
                  >
                    <MenuItem value="">
                      <em>{t('form.resolutionYears.placeholder')}</em>
                    </MenuItem>
                    {yearOptions.map((year) => (
                      <MenuItem key={year} value={String(year)}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={styles.submitBelowYears}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSubmit(onSubmit)}
                >
                  {t('initialFilter.submitButton')}
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <>
      <Grid container alignItems="center" sx={styles.titleRow}>
        <Grid item xs>
          <Typography variant="h3">{t('initialFilter.title')}</Typography>
        </Grid>
        <Grid item>
          <Box sx={styles.caseHadingBoxWrapper}>
            <Typography variant="h4" sx={{ my: 0 }}>
              {t('caseHading.title')}
              {formatNumber(includeStatisticsData?.total as number)}
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <Paper sx={styles.wrapper}>
        <Grid container>{renderForm()}</Grid>
      </Paper>
    </>
  );
};
